import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';

import { Apollo } from 'apollo-angular';

import {
  getCookiesPreference,
  getRedirectUrl,
  Go,
  KeycloakService,
  LocalStorageService,
  LOGIN_KEYCLOAK,
  LoginKeycloak,
  REQUEST_PASSWORD_RESET,
  RequestPasswordReset,
  RequestPasswordResetFail,
  RequestPasswordResetSuccess,
  RESET_PASSWORD,
  RESET_PASSWORD_SUCCESS,
  RESET_PASSWORD_SUCCESS_MESSAGE,
  ResetPassword,
  ResetPasswordFail,
  ResetPasswordSuccess,
  SessionStorageService,
  ThemeService,
  UPDATE_APPLICATION_SUCCESS,
  UpdateApplicationSuccess,
  UpdateCookieTracking,
  USER_LOGIN,
  USER_LOGIN_FAIL,
  USER_LOGIN_SUCCESS,
  USER_LOGOUT,
  USER_REDIRECT,
  USER_UPDATE_LAST_LOGIN,
  UserLogin,
  UserLoginFail,
  UserLoginSuccess,
  UserLogout,
  UserRedirect,
  UserUpdateLastLoginSuccess,
  WINDOW_REF
} from '@ui/legacy-lib';

import { from, of } from 'rxjs';
import {
  APPLY_INTENT_STORAGE_KEYS,
  storageKeys
} from '@ui/tenant-pool/shared/config/util';
import { UserStore } from '@ui/tenant-pool/shared/user/data-access';
import { FormStorageService } from '../../auth/services/form-storage.service';
import {
  requestPasswordResetMutation,
  resetPasswordMutation,
  ssoQuery,
  userUpdateLastLogin
} from '../../core/queries/auth.queries';
import { MainPageNavigation } from '../../config/navigation.config';

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private keycloakService = inject(KeycloakService);
  private apollo = inject(Apollo);
  private sessionStorage = inject(SessionStorageService);
  private localStorageService = inject(LocalStorageService);
  private formStorageService = inject(FormStorageService);
  private store = inject(Store);
  private userStore = inject(UserStore);
  private windowRef = inject(WINDOW_REF);
  private themeService = inject(ThemeService);

  userLoginFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(USER_LOGIN_FAIL),
      map(() => new UserRedirect())
    )
  );

  userRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UserRedirect>(USER_REDIRECT),
      map(
        ({ pathAfterAuth }) =>
          new Go({
            path: [MainPageNavigation.LOGIN],
            query: { pathAfterAuth }
          })
      )
    )
  );

  resetPasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ResetPasswordSuccess>(RESET_PASSWORD_SUCCESS),
      map(() => {
        return new Go({
          path: ['/login'],
          query: { message: RESET_PASSWORD_SUCCESS_MESSAGE }
        });
      })
    )
  );

  userUpdateLastLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(USER_UPDATE_LAST_LOGIN),
      switchMap(() =>
        this.apollo
          .mutate({ mutation: userUpdateLastLogin })
          .pipe(map(() => new UserUpdateLastLoginSuccess()))
      )
    )
  );

  requestPasswordReset$ = createEffect(() =>
    this.actions$.pipe(
      ofType<RequestPasswordReset>(REQUEST_PASSWORD_RESET),
      switchMap(action =>
        this.apollo
          .mutate({
            mutation: requestPasswordResetMutation,
            variables: { email: action.email }
          })
          .pipe(
            map(() => new RequestPasswordResetSuccess()),
            catchError(err => [
              new RequestPasswordResetFail(new Error(err.message))
            ])
          )
      )
    )
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ResetPassword>(RESET_PASSWORD),
      map(action => action.resetPasswordPayload),
      switchMap(payload =>
        this.apollo
          .mutate({
            mutation: resetPasswordMutation,
            variables: {
              password: payload.password,
              confirmedPassword: payload.confirmedPassword,
              token: payload.token
            }
          })
          .pipe(
            map(res => {
              const successCodes = [200, 201, 202];
              if (!successCodes.includes(res.data.resetPassword.status)) {
                return new ResetPasswordFail(
                  new Error('ERR_SETTING_NEW_PASSWORD')
                );
              }

              return new ResetPasswordSuccess();
            }),
            catchError(error => [new ResetPasswordFail(error)])
          )
      )
    )
  );

  userLoginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UserLoginSuccess>(USER_LOGIN_SUCCESS),
      map(({ stateAfterAuth }) => {
        this.formStorageService.clear();
        this.sessionStorage.removeItem(storageKeys.propertyData);
        return new Go({
          path: [stateAfterAuth.pathAfterAuth],
          query: stateAfterAuth.queryParams
        });
      })
    )
  );

  // We need updateLastLogin for an anonymous account check, Backend handle all important stuff.
  updateLastLoginSucces$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateApplicationSuccess>(UPDATE_APPLICATION_SUCCESS),
      switchMap(() => this.store.select(getCookiesPreference)),
      map(cookiePreference => new UpdateCookieTracking(cookiePreference))
    )
  );

  userLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UserLogin>(USER_LOGIN),
      switchMap(action => {
        const { email, redirectUri, loginMethod } = action.userCredentials;

        return this.apollo
          .query({
            query: ssoQuery,
            variables: { email, redirectUri, loginMethod }
          })
          .pipe(
            concatMap(result => {
              const { realm, clientId, baseUrl } = result.data.sso;

              this.localStorageService.setItem('realm', realm);
              this.localStorageService.setItem('clientId', clientId);

              return from(
                this.keycloakService.init(
                  {
                    url: baseUrl,
                    realm,
                    clientId
                  },
                  { redirectUri }
                )
              );
            }),
            map(authenticated => {
              if (authenticated) {
                const { pathname, searchParams } = new URL(redirectUri);

                return new UserLoginSuccess(undefined, {
                  pathAfterAuth: pathname,
                  queryParams: Object.fromEntries(searchParams)
                });
              } else {
                return new LoginKeycloak({
                  loginHint: email,
                  idpHint: loginMethod.toLowerCase()
                });
              }
            }),
            catchError((err: Error) => of(new UserLoginFail(err.message)))
          );
      })
    )
  );

  loginKeycloak$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<LoginKeycloak>(LOGIN_KEYCLOAK),
        tap(({ loginOptions }) =>
          from(this.keycloakService.login(loginOptions))
        )
      ),
    { dispatch: false }
  );

  userLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<UserLogout>(USER_LOGOUT),
        tap(action => {
          // Read inside the tap, not before the pipe: `withLatestFrom` took a
          // snapshot at emission time, and the signal has to be read at the
          // same point to keep that.
          const { internalPoolReturnUrl } = this.userStore.userData() || {
            internalPoolReturnUrl: ''
          };
          const redirectUri =
            internalPoolReturnUrl ||
            action.redirectUrl ||
            getRedirectUrl(
              this.windowRef.location.toString(),
              '/login?loggedOut=true',
              null
            );
          this.themeService.resetTheme();
          void this.keycloakService.logout(redirectUri);
          APPLY_INTENT_STORAGE_KEYS.forEach(key =>
            this.sessionStorage.removeItem(key)
          );
        })
      ),
    { dispatch: false }
  );
}
